import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "../../contexts/TenantContext";
import { authService } from "../../services/authService";
import { logger } from "../../utils/logger";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  squeezeRegisterSchema,
  SqueezeRegisterFormData,
} from "../../schemas/auth";
import { resolveIdxConsentStyle } from "../../config/consentStyle";
import SplitConsentCheckboxes from "./SplitConsentCheckboxes";
import { useIdxConsentProfile } from "../../hooks/auth/useIdxConsentProfile";
import {
  captureDisplayedSplitConsent,
  type IdxConsentReceipt,
  type SplitConsentSnapshot,
} from "../../config/idxConsentReceipts";

interface RegisterFormProps {
  isLoading?: boolean;
}

export const RegisterForm = ({
  isLoading: propIsLoading,
}: RegisterFormProps) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [textConsent, setTextConsent] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const consentSnapshotRef = useRef<SplitConsentSnapshot | null>(null);
  const navigate = useNavigate();
  const { config } = useTenant();
  const { profile: idxConsentProfile, loading: idxConsentProfileLoading } =
    useIdxConsentProfile(config?.id);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<SqueezeRegisterFormData>({
    resolver: zodResolver(squeezeRegisterSchema),
    mode: "onChange",
  });

  // Combine loading states
  const formLoading = isLoading || propIsLoading || idxConsentProfileLoading;

  if (!config) {
    return <div>Loading...</div>;
  }

  // Default branding values
  const defaultBranding = {
    colors: {
      background: "#ffffff",
      text: "#000000",
      primary: "#000000",
      secondary: "#666666",
    },
    logo: {
      url: "/placeholder-logo.png",
      width: 100,
      height: 50,
    },
    fonts: {
      primary: "system-ui",
    },
  };

  // Use config branding if available, otherwise use defaults
  const branding = config?.branding || defaultBranding;
  const logo = branding.logo ?? defaultBranding.logo;

  // The server's FUB capture scope selects the existing split groups. Other
  // orgs retain their configured style or the SPEC-2232 fallback.
  const consentStyle = resolveIdxConsentStyle(
    config.idxConsentStyle,
    idxConsentProfile?.capture_enabled === true
  );

  // One bundled checkbox covering all three channels, which blocks submission.
  const showConsentCheckbox = consentStyle === "bundled";

  // Two optional checkboxes splitting the text opt-in from calls-and-email.
  const showSplitConsent = consentStyle === "split";

  // Whenever a box is on screen its value is the consent of record; only the
  // passive treatment, which renders no box at all, implies consent.
  const consentIsExplicit = consentStyle !== "passive";

  // The bundled checkbox is the only one that gates the form: the split boxes
  // stay optional and passive orgs have nothing to tick.
  const consentRequired = showConsentCheckbox;
  const consentMissing = consentRequired && !consent;

  const onSubmit = async (data: SqueezeRegisterFormData) => {
    if (idxConsentProfileLoading) return;
    if (consentMissing) {
      setError("Please agree to be contacted before continuing.");
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      let consentReceipts: IdxConsentReceipt[] | undefined;
      if (idxConsentProfile?.capture_enabled === true) {
        const snapshot = await captureDisplayedSplitConsent(
          formRef.current,
          {
            email: data.email,
            phoneNumber: data.phoneNumber,
            callsAndEmailConsent: consent,
            textConsent,
            surface: "idx-register",
            sourceUrl: window.location.href,
          },
          consentSnapshotRef.current
        );
        consentSnapshotRef.current = snapshot;
        consentReceipts = snapshot.receipts;
      }
      // Register user through authService
      await authService.register({
        email: data.email,
        password: data.phoneNumber, // Use phone number as password
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
        marketingConsent: consentIsExplicit ? consent : true,
        consentReceipts,
        tenantId: config.id,
      });

      // AuthProvider will handle navigation after profile creation
    } catch (err) {
      logger.error("[Registration Flow]", { code: "REGISTRATION_FAILED" });
      setError(
        err instanceof Error
          ? err.message
          : "An error occurred during registration"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-[calc(100vh-100px)] lg:min-h-[calc(100vh-200px)] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundColor: branding.colors.background,
        color: branding.colors.text,
      }}
    >
      <div className="max-w-md w-full space-y-8">
        <div>
          <img
            src={logo.url}
            alt={config?.name || "Logo"}
            style={{
              height: logo.height,
              width: "auto",
              maxWidth: logo.width,
              objectFit: "contain",
            }}
            className="mx-auto"
          />
          <h2
            className="mt-6 text-center text-2xl font-bold"
            style={{ color: branding.colors.text }}
          >
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{" "}
            <button
              onClick={() => navigate("/auth/login")}
              className="font-medium hover:opacity-80 !bg-transparent underline p-0 pl-2"
              style={{ color: branding.colors.primary }}
            >
              Sign In to your existing account
            </button>
          </p>
        </div>

        <form
          ref={formRef}
          noValidate
          className="mt-8 space-y-6"
          onChange={() => {
            consentSnapshotRef.current = null;
          }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-medium text-gray-700"
                >
                  First Name
                </label>
                <input
                  id="firstName"
                  type="text"
                  {...register("firstName")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] sm:text-sm"
                  disabled={formLoading}
                />
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.firstName.message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-medium text-gray-700"
                >
                  Last Name
                </label>
                <input
                  id="lastName"
                  type="text"
                  {...register("lastName")}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] sm:text-sm"
                  disabled={formLoading}
                />
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.lastName.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                {...register("email")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] sm:text-sm"
                disabled={formLoading}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="phoneNumber"
                className="block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>
              <input
                id="phoneNumber"
                type="tel"
                {...register("phoneNumber")}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[var(--primary)] focus:ring-[var(--primary)] sm:text-sm"
                disabled={formLoading}
              />
              {errors.phoneNumber && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.phoneNumber.message}
                </p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Your phone number will be used as your password
              </p>
            </div>
          </div>

          {showSplitConsent ? (
            <SplitConsentCheckboxes
              orgName={config.name}
              callsAndEmailConsent={consent}
              onCallsAndEmailConsentChange={setConsent}
              textConsent={textConsent}
              onTextConsentChange={setTextConsent}
              disabled={formLoading}
              idPrefix="register"
              className="mt-4 text-[0.65rem] lg:text-[0.7rem] text-gray-600 leading-snug"
            />
          ) : (
            <div className="mt-4 text-[0.65rem] lg:text-[0.7rem] text-gray-600 leading-snug">
              {showConsentCheckbox && (
                <input
                  id="register-consent"
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  disabled={formLoading}
                  className="mr-1.5 inline-block h-4 w-4 align-[-3px] rounded border-gray-300 text-[var(--primary)] focus:ring-[var(--primary)]"
                />
              )}
              <span
                className={showConsentCheckbox ? "cursor-pointer" : undefined}
                onClick={(e) => {
                  // Toggle consent on text click (only when the checkbox is shown);
                  // let policy links behave normally.
                  if (!showConsentCheckbox) return;
                  if ((e.target as HTMLElement).closest("a")) return;
                  if (!formLoading) setConsent(!consent);
                }}
              >
                {showConsentCheckbox
                  ? "Yes, I agree to be contacted by "
                  : "By submitting this form, you agree to be contacted by "}
                {config.name}{" "}
                via call, email, and text for real estate services, including
                robocalls, automated calls and texts, and AI-powered communications.
                You understand that consent is not required to purchase and that you
                can opt out at any time. To opt out, you can reply 'stop' at any
                time. You can also click the unsubscribe link in the emails. Data
                will not be sold or shared with third parties for promotional or
                marketing purposes. Message and data rates may apply. Message
                frequency may vary.{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-800 underline"
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-blue-800 underline"
                >
                  Privacy Policy
                </a>
                .
              </span>
            </div>
          )}

          {error && (
            <p className="text-sm font-medium text-red-600">{error}</p>
          )}

          <div>
            <button
              type="submit"
              disabled={formLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50"
            >
              {idxConsentProfileLoading
                ? "Loading consent options..."
                : formLoading
                  ? "Creating account..."
                  : "Create account"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RegisterForm;
